import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { TourProgress } from '@vd/types';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useRecordingUnderWay } from '../recorder/RecorderProvider';
import { afterSection, atPath, offeredHere, offersWanted, registeredTours, resumeAt, stepBack, stepFor, stepNumber, stepPath, tourOffer, tourScope, TOURS_ENABLED, type TourAudience, type TourContext, type TourDefinition, type TourOffer, type TourStep } from './tours';
import { TourSpotlight } from './TourSpotlight';
import { TourOfferCard } from './TourOfferCard';
import { modalOpen, topmostModal, useModalOpen } from './modalOpen';

/** What can take focus inside a window, for focus left there when the tour ends. */
const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
import { DOCK_ATTRIBUTE, shrinkIntoDock } from './dock';

/**
 * Runs the onboarding tours (TOADO-971): which one is showing, at which
 * step, and the person's stored progress for each.
 *
 * One tour at a time, and none started while a recording is under way or a
 * modal dialog is open, since a tour pointing past either would point at
 * something the person cannot reach. Nothing is offered or started until
 * the stored progress has loaded: an offer made without it would say Start
 * and write step 0 over a tour in progress.
 *
 * A running tour stays where it was started: it keeps the workspace (and,
 * for a project tour, the project) it was launched in, pauses anywhere else,
 * and writes its progress against that scope only. Progress is written at
 * every step, one write at a time per tour, in order, so a later step or a
 * skip is never overtaken by an earlier write, and the server keeps them in
 * that order too (TourWriter). A write that fails is not shown: this page
 * goes on from what it wrote, and the next step writes again.
 *
 * A tour with an `offer` is offered once, on its route, to someone with no
 * progress stored for it (TOADO-981): after a short wait, so it does not
 * flash past during a navigation, and never during a recording, over a
 * modal or while a tour runs. Taking it stores progress, and declining it
 * stores `declined`, so either way it is not offered again. A tour ending,
 * and an offer declined, shrink into Get started.
 */
interface ActiveTour {
  tour: TourDefinition;
  step: number;
  /** Where it was started, which it resumes in and writes against. */
  launchedIn: TourContext;
  /**
   * Whether the workspace had a project when it started (Jason, 28 Oct): its
   * steps are numbered with or without making the first one for the whole run,
   * so the count does not change under the person once they have made it.
   */
  startedWithProject: boolean;
  /** Whether it has one now: the first project's steps are never gone back to once it does. */
  hasProject: boolean;
  /** Where focus was when the tour started, given back when it ends. */
  opener: HTMLElement | null;
}

interface TourContextValue {
  enabled: boolean;
  tours: readonly TourDefinition[];
  active: { tourId: string; step: number } | null;
  /** Set while a recording is under way: no tour starts, and a running one pauses. */
  recording: boolean;
  /** Set while a modal dialog is open: no tour starts, and a running one hides. */
  underModal: boolean;
  /** A launch on the page says so while mounted, so modals are watched only while one could start a tour. */
  registerLaunch(): () => void;
  /** What a tour offers here, or null until progress has loaded (or for a tour there is not). */
  offerFor(tourId: string): TourOffer | null;
  /**
   * Starts, resumes or replays a tour, as its offer says, or from its first
   * step when `fromStart` (Start over, Jason 28 Oct). False when one cannot
   * start now.
   */
  start(tourId: string, fromStart?: boolean): boolean;
}

const TourContextObject = createContext<TourContextValue | null>(null);

const NO_TOURS: TourContextValue = { enabled: false, tours: [], active: null, recording: false, underModal: false, offerFor: () => null, start: () => false, registerLaunch: () => () => {} };

/** The tours, or none where no provider is mounted (a page under test, the widget's own frames). */
export function useTours(): TourContextValue {
  return useContext(TourContextObject) ?? NO_TOURS;
}

/**
 * Whether `here` is inside the scope a tour was launched in. A project tour
 * started where there is no project (Home) belongs to no project yet: it
 * runs anywhere in its workspace until it binds to the first one reached.
 * A page with no project of its own (workspace settings), or a ticket whose
 * project is not loaded yet, is not another project: the board tour goes
 * through both (TOADO-988). A loaded ticket carries its project in `here`.
 */
/** A ticket's project as a board list in the cache has it (TOADO-988): a list is an array of ticket summaries, or carries one as `items`. */
function projectFromLists(qc: QueryClient, ticketId: string): string | undefined {
  for (const [, data] of qc.getQueriesData<unknown>({ queryKey: ['tickets'] })) {
    const items = Array.isArray(data) ? data : (data as { items?: unknown } | undefined)?.items;
    if (!Array.isArray(items)) continue;
    const found = items.find((item) => (item as { id?: unknown } | null)?.id === ticketId) as { projectId?: unknown } | undefined;
    if (typeof found?.projectId === 'string') return found.projectId;
  }
  return undefined;
}

/** Whether a tour step's control is on the page now, for Back passing over a step it would meet (TOADO-988). */
const anchorOnPage = (anchor: string) => document.querySelector(`[data-tour-anchor="${CSS.escape(anchor)}"]`) !== null;

function inLaunchScope(tour: TourDefinition, launchedIn: TourContext, here: TourContext): boolean {
  if (tour.scope === 'none') return true;
  if (here.companyId !== launchedIn.companyId) return false;
  return tour.scope !== 'project' || launchedIn.projectId === null || here.projectId === null || here.projectId === launchedIn.projectId;
}

/** How long one progress write may take before the next goes ahead; it is aborted then. */
export const WRITE_WAIT_MS = 20_000;

/** How long someone is on an offer's route before the offer shows. */
export const OFFER_DELAY_MS = 1_200;

/** One name per open page, for the server to keep this page's writes in the order it made them. */
function newPageId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `page-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** The request, aborted if it has not answered within `ms`. */
function withDeadline<T>(send: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return send(controller.signal).finally(() => clearTimeout(timer));
}

export function TourProvider({ children }: { children: ReactNode }) {
  const { user, currentCompanyId, companies } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  /**
   * The sample ticket this tour made (TOADO-988, Jason 28 Oct): on the first
   * board it reaches, and gone when it ends, so no board keeps one. A tour
   * resumed after closing makes a fresh one.
   */
  const sampleRef = useRef<{ projectId: string; ticketId: string | null } | null>(null);
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;
  const searchRef = useRef(location.search);
  searchRef.current = location.search;
  const endSample = useCallback(() => {
    const sample = sampleRef.current;
    sampleRef.current = null;
    if (!sample) return;
    // Off the sample first, if it is open: it is about to go.
    if (sample.ticketId && matchPath('/tickets/:ticketId', locationRef.current)?.params.ticketId === sample.ticketId) {
      navigate(`/projects/${sample.projectId}/board`);
    }
    void api.endTourSample(sample.projectId)
      .then(() => qc.invalidateQueries({ queryKey: ['tickets', sample.projectId] }))
      .catch(() => undefined);
  }, [navigate, qc]);
  // An owner's steps only for an owner (TOADO-988): a member cannot open the
  // widget's or Jira's settings, so their tour passes over them (`stepFor`).
  // The role in the workspace the tour is about, never in another one.
  const ownerIn = useCallback((companyId: string | null) => companies?.find((company) => company.id === companyId)?.role === 'owner', [companies]);
  /** Who is taking a running tour, for which of its steps are theirs. */
  const audience = useCallback((current: ActiveTour): TourAudience => ({ owner: ownerIn(current.launchedIn.companyId), hasProject: current.hasProject }), [ownerIn]);
  const recording = useRecordingUnderWay();
  // Launches on the page now: with none and no tour showing, nothing watches for modals.
  const [launches, setLaunches] = useState(0);
  const registerLaunch = useCallback(() => {
    setLaunches((count) => count + 1);
    return () => setLaunches((count) => count - 1);
  }, []);
  const tours = useMemo(() => (TOURS_ENABLED ? registeredTours() : []), []);
  const offers = useMemo(() => offersWanted(), []);
  const [active, setActive] = useState<ActiveTour | null>(null);
  // The latest active tour, for handlers that run before a render.
  const activeRef = useRef<ActiveTour | null>(null);
  activeRef.current = active;
  // Each tour's writes, chained so they go in the order made, and numbered
  // under this page's name, so the server stores them in that order too.
  const writes = useRef(new Map<string, Promise<unknown>>());
  const writer = useRef({ page: newPageId(), seq: 0 });
  // Where focus goes once the tour's box has gone and the page has re-rendered.
  const [returnFocusTo, setReturnFocusTo] = useState<{ opener: HTMLElement | null; tourId: string } | null>(null);

  // Loaded once any answer is in hand: a refetch that fails keeps the last
  // answer, and the tours keep working from it.
  const { data: homeState } = useQuery({
    queryKey: ['home-state', currentCompanyId],
    queryFn: () => api.getHomeState(currentCompanyId),
    enabled: !!user && !!currentCompanyId && tours.length > 0,
  });
  const progressLoaded = homeState !== undefined;
  // A tour this page has written stands as this page wrote it, whatever a
  // read says after: no answer to a read can show whether it left before
  // or after a write, so none is let replace one. Progress made in another
  // tab shows here on the next load, which is the cost, and a small one: a
  // tour is taken in one tab.
  const [written, setWritten] = useState<Record<string, TourProgress>>({});
  const stored = useMemo(() => ({ ...homeState?.tours, ...written }), [homeState?.tours, written]);

  /**
   * A ticket's address names no project, so its project is read from the
   * ticket as the page loaded it (TOADO-988): a ticket of another project
   * pauses a project tour rather than being toured as its own. Read from the
   * cache only, never fetched for the tour; its controls arrive with it.
   */
  const ticketId = matchPath('/tickets/:ticketId', location.pathname)?.params.ticketId;
  const { data: openTicket, status: openTicketStatus } = useQuery<{ projectId?: string }>({
    queryKey: ['ticket', ticketId],
    queryFn: () => api.getTicket(ticketId!),
    enabled: false,
  });
  /**
   * The ticket's project as a board's list already knows it, so a ticket
   * opened from the board is placed at once rather than after its fetch
   * (review round 6). Read on each render, which a navigation is.
   */
  const listedProject = ticketId && openTicket?.projectId === undefined ? projectFromLists(qc, ticketId) : undefined;
  const ticketProject = openTicket?.projectId ?? listedProject;
  // On a ticket whose project is not known yet: the tour neither shows nor moves
  // (TOADO-988). A ticket that failed to load is not waited for.
  const scopePending = !!ticketId && ticketProject === undefined && openTicketStatus !== 'error';
  const here: TourContext = useMemo(() => ({
    companyId: currentCompanyId,
    projectId: matchPath('/projects/:projectId/*', location.pathname)?.params.projectId
      ?? (ticketId ? ticketProject ?? null : null),
  }), [currentCompanyId, location.pathname, ticketId, ticketProject]);
  const hereRef = useRef(here);
  hereRef.current = here;
  const lastProjectRef = useRef<string | null>(null);
  lastProjectRef.current = homeState?.lastProjectId ?? null;

  /**
   * The address of a step's page (Jason, 28 Oct): the project the person is
   * in, else the sample's, else the one the tour was started in, else the
   * one they opened last; the ticket is the sample. Null when it needs one
   * the tour does not know.
   */
  const pathFor = useCallback((step: TourStep | undefined, launchedIn?: TourContext): string | null => {
    if (!step) return null;
    const projectId = hereRef.current.projectId ?? sampleRef.current?.projectId ?? launchedIn?.projectId ?? lastProjectRef.current;
    return stepPath(step, { projectId, ticketId: sampleRef.current?.ticketId ?? null });
  }, []);

  /** Takes the person to a step's page, if the tour knows it and they are not on it already. */
  const goToPage = useCallback((step: TourStep | undefined, launchedIn: TourContext, always: boolean) => {
    if (!step || !(always || step.open)) return;
    const path = pathFor(step, launchedIn);
    if (!path) return;
    const there = step.open
      ? atPath(path, locationRef.current, searchRef.current)
      : !!step.route && matchPath(step.route, locationRef.current) !== null;
    if (!there) navigate(path);
  }, [pathFor, navigate]);

  // The tour to offer here, if any: on its route, with nothing stored for it.
  // Once per person, not per project, though the board tour is scoped to a
  // project: the offer is how someone new learns the tours exist, and Get
  // started has the tour for every board after that (Jason, 28 Oct).
  const offerable = useMemo(() => (offers && progressLoaded && !active
    ? tours.find((tour) => tour.offer && offeredHere(stored[tour.id], here) && matchPath(tour.offer.route, location.pathname) !== null) ?? null
    : null), [offers, progressLoaded, active, tours, stored, location.pathname, here]);
  const underModal = useModalOpen(tours.length > 0 && (active !== null || launches > 0 || offerable !== null));
  // Shown once the person has been on the route a moment, the wait starting again on each route.
  const [settledOn, setSettledOn] = useState<string | null>(null);
  useEffect(() => {
    setSettledOn(null);
    if (!offerable) return;
    const path = location.pathname;
    const timer = window.setTimeout(() => setSettledOn(path), OFFER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [offerable, location.pathname]);
  const offerShown = offerable && settledOn === location.pathname && !recording && !underModal ? offerable : null;

  const save = useCallback((tour: TourDefinition, progress: Omit<TourProgress, 'updatedAt'>) => {
    // Read here at once. The writes of one tour go one at a time, each given
    // WRITE_WAIT_MS before it is abandoned, so one that never answers cannot
    // hold up the next; one abandoned and committed late is refused by the
    // server, which stores this page's writes in the order they are numbered.
    writer.current.seq += 1;
    const named = { ...progress, writer: { page: writer.current.page, seq: writer.current.seq } };
    setWritten((current) => ({ ...current, [tour.id]: { ...progress, updatedAt: new Date().toISOString() } }));
    const before = writes.current.get(tour.id) ?? Promise.resolve();
    const write = before.then(() => withDeadline((signal) => api.saveTourProgress(tour.id, named, signal), WRITE_WAIT_MS)).catch(() => undefined);
    writes.current.set(tour.id, write);
  }, []);

  const offerFor = useCallback((tourId: string): TourOffer | null => {
    const tour = tours.find((candidate) => candidate.id === tourId);
    return tour && progressLoaded ? tourOffer(tour, stored?.[tourId], here) : null;
  }, [tours, stored, here, progressLoaded]);

  const start = useCallback((tourId: string, fromStart = false): boolean => {
    const tour = tours.find((candidate) => candidate.id === tourId);
    if (!tour || !progressLoaded || activeRef.current || recording || modalOpen()) return false;
    const offer = tourOffer(tour, stored?.[tourId], here);
    const resuming = offer.kind === 'resume' && !fromStart;
    // Past any owner's step a member cannot reach, from where it resumes; with
    // none left to reach, it does not start (TOADO-988). Inside a section, from
    // its start, since what it stands on is made again (Jason, 28 Oct).
    const hasProject = !!homeState?.hasProject || here.projectId !== null;
    const step = stepFor(tour, resuming ? resumeAt(tour, offer.step) : 0, 1, { owner: ownerIn(here.companyId), hasProject });
    if (step >= tour.steps.length) return false;
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : null;
    // Resumed from a page with no project (workspace settings), it stays bound
    // to the project it was taken in, so another project still pauses it.
    const resumedProject = resuming ? stored?.[tourId]?.scope?.projectId : undefined;
    const launchedIn: TourContext = resumedProject && here.projectId === null ? { ...here, projectId: resumedProject } : here;
    const next: ActiveTour = { tour, step, launchedIn, opener, startedWithProject: hasProject, hasProject };
    activeRef.current = next;
    setActive(next);
    save(tour, { version: tour.version, state: 'in_progress', step, scope: tourScope(tour, launchedIn) });
    // To the step's page, wherever it was started from (Jason, 28 Oct): resumed
    // from Get started on another page, it used to wait there with no way on.
    goToPage(tour.steps[step], launchedIn, true);
    return true;
  }, [tours, stored, here, recording, save, progressLoaded, ownerIn, goToPage, homeState?.hasProject]);

  const finish = useCallback((state: 'completed' | 'skipped' | 'closed') => {
    const current = activeRef.current;
    if (!current) return;
    endSample();
    // Measured while it is still on the page: what shows of the tour, its box or its pause, goes into Get started.
    const id = CSS.escape(current.tour.id);
    shrinkIntoDock(document.querySelector(`[data-tour-box="${id}"], [data-tour-paused="${id}"]`));
    activeRef.current = null;
    setActive(null);
    // Closing keeps the step, so it resumes there; skipping and finishing start over next time.
    if (state !== 'closed') save(current.tour, { version: current.tour.version, state, step: 0, scope: tourScope(current.tour, current.launchedIn) });
    setReturnFocusTo({ opener: current.opener, tourId: current.tour.id });
  }, [save, endSample]);

  // After the render that took the box away and enabled the launch buttons
  // again: back to where the tour was started from, or to its launch button
  // if that has gone, or else to Get started, where the tour went; nowhere
  // if none of them is on the page. With a window still open, focus stays in
  // it (review round 9): ended from the tour's copy inside a ticket, the place
  // it started from is behind a window that still holds the page.
  useEffect(() => {
    if (!returnFocusTo) return;
    const { opener, tourId } = returnFocusTo;
    setReturnFocusTo(null);
    const usable = (node: HTMLElement | null) => !!node?.isConnected && !(node as HTMLButtonElement).disabled && node.getAttribute('aria-disabled') !== 'true';
    const target = usable(opener) ? opener : (document.querySelector<HTMLElement>(`[data-tour-launch="${CSS.escape(tourId)}"]:not(:disabled):not([aria-disabled="true"])`)
      ?? document.querySelector<HTMLElement>(`[${DOCK_ATTRIBUTE}]`));
    const top = topmostModal();
    if (top && !(target && top.contains(target))) {
      (top.querySelector<HTMLElement>(FOCUSABLE) ?? (top instanceof HTMLElement ? top : null))?.focus();
      return;
    }
    target?.focus();
  }, [returnFocusTo]);

  // Turned down: stored as such, so it is never offered on its own again,
  // and shrunk into Get started, where it can still be found.
  const decline = useCallback((tour: TourDefinition, card: HTMLElement | null) => {
    shrinkIntoDock(card);
    save(tour, { version: tour.version, state: 'declined', step: 0, scope: tourScope(tour, here) });
  }, [save, here]);

  // The sample, made once the tour is on a board of its workspace (TOADO-988).
  // A board the person cannot write to makes none, and the tour's ticket part
  // says it cannot be shown and offers to skip it, as it always has.
  useEffect(() => {
    if (!active?.tour.sample || sampleRef.current || !here.projectId || here.companyId !== active.launchedIn.companyId) return;
    const projectId = here.projectId;
    const sample: { projectId: string; ticketId: string | null } = { projectId, ticketId: null };
    sampleRef.current = sample;
    void api.startTourSample(projectId)
      .then(({ id }) => {
        sample.ticketId = id;
        return qc.invalidateQueries({ queryKey: ['tickets', projectId] });
      })
      .catch(() => {
        // Nothing to take away; the step that needs it waits, then offers Skip.
        if (sampleRef.current === sample) sampleRef.current = null;
      });
  }, [active, here.projectId, here.companyId, qc]);

  // A project tour started without a project takes the first one the person
  // opens in its workspace, and from then on resumes and writes there.
  useEffect(() => {
    const current = activeRef.current;
    if (!current || current.tour.scope !== 'project' || current.launchedIn.projectId !== null) return;
    if (!here.projectId || here.companyId !== current.launchedIn.companyId) return;
    const bound = { ...current, launchedIn: { ...current.launchedIn, projectId: here.projectId } };
    activeRef.current = bound;
    setActive(bound);
    save(bound.tour, { version: bound.tour.version, state: 'in_progress', step: bound.step, scope: tourScope(bound.tour, bound.launchedIn) });
  }, [here, save]);

  const goTo = useCallback((step: number) => {
    const current = activeRef.current;
    if (!current) return;
    if (step >= current.tour.steps.length) {
      finish('completed');
      return;
    }
    const next = { ...current, step: Math.max(0, step) };
    activeRef.current = next;
    setActive(next);
    // Against where it was started: a paused tour cannot advance, so today
    // this is also where the person is, but the scope is the launch's to say.
    save(current.tour, { version: current.tour.version, state: 'in_progress', step: next.step, scope: tourScope(current.tour, current.launchedIn) });
    // A step with a page of its own takes the person there (Jason, 28 Oct).
    goToPage(current.tour.steps[next.step], current.launchedIn, false);
  }, [finish, save, goToPage]);

  // Someone no longer an owner of the tour's workspace, on an owner's step,
  // there: on to the next they can reach (TOADO-988). Never from another
  // workspace, and never past the end, which would call it completed.
  useEffect(() => {
    const current = activeRef.current;
    if (!current || !current.tour.steps[current.step]?.ownerOnly) return;
    if (!inLaunchScope(current.tour, current.launchedIn, here) || ownerIn(current.launchedIn.companyId)) return;
    const next = stepFor(current.tour, current.step, 1, audience(current));
    if (next < current.tour.steps.length) goTo(next);
  }, [ownerIn, audience, active, here, goTo]);

  // The workspace's first project made, in the tour or beside it (Jason, 28 Oct):
  // from here its steps are not the person's to go back to. Known by a board of
  // the tour's workspace, or the workspace's own answer.
  useEffect(() => {
    const current = activeRef.current;
    if (!current || current.hasProject) return;
    const made = (here.projectId !== null && here.companyId === current.launchedIn.companyId)
      || (!!homeState?.hasProject && currentCompanyId === current.launchedIn.companyId);
    if (!made) return;
    const next = { ...current, hasProject: true };
    activeRef.current = next;
    setActive(next);
  }, [active, here, homeState?.hasProject, currentCompanyId]);

  const value = useMemo<TourContextValue>(() => ({
    enabled: tours.length > 0,
    tours,
    active: active ? { tourId: active.tour.id, step: active.step } : null,
    recording,
    underModal,
    offerFor,
    start,
    registerLaunch,
  }), [tours, active, recording, underModal, offerFor, start, registerLaunch]);

  return (
    <TourContextObject.Provider value={value}>
      {children}
      {offerShown && (
        <TourOfferCard
          key={offerShown.id}
          tour={offerShown}
          onStart={() => { start(offerShown.id); }}
          onDecline={(card) => decline(offerShown, card)}
        />
      )}
      {active && (
        <TourSpotlight
          tour={active.tour}
          step={active.step}
          elsewhere={!inLaunchScope(active.tour, active.launchedIn, here) ? active.tour.scope : null}
          recording={recording}
          scopePending={scopePending}
          ticketFailed={!!ticketId && openTicketStatus === 'error'}
          underModal={underModal}
          stepPage={pathFor(active.tour.steps[active.step], active.launchedIn)}
          // Counted among the steps of this person's tour, as it started (Jason, 28 Oct).
          position={stepNumber(active.tour, active.step, { owner: ownerIn(active.launchedIn.companyId), hasProject: active.startedWithProject })}
          canGoBack={stepFor(active.tour, active.step - 1, -1, audience(active)) >= 0}
          onBack={() => { if (!scopePending) goTo(stepBack(active.tour, active.step, audience(active), location.pathname, anchorOnPage)); }}
          onNext={() => { if (!scopePending) goTo(stepFor(active.tour, active.step + 1, 1, audience(active))); }}
          onSkip={() => { if (!scopePending) finish('skipped'); }}
          onSkipSection={() => { if (!scopePending) goTo(stepFor(active.tour, afterSection(active.tour, active.step), 1, audience(active))); }}
          onClose={() => finish('closed')}
        />
      )}
    </TourContextObject.Provider>
  );
}
